import { useState, useEffect, useRef } from "react";
import { Box, Typography, Grid, Container } from "@mui/material";
import RememberYourResults from "../Dialogs/RememberYourResults";
import AssessmentSummary from "./AssessmentSummary";
import BookAppointmentForm from "./BookAppointmentForm";
import BookedAppointmentCard from "./BookedAppointmentCard";
import SurveyResultsButton from "../UserInput/Buttons/SurveyResultsButton";
import { COPYRIGHT_MESSAGE } from "../../constants";
import SurveyLogo from "./SurveyLogo";
import SurveyButton from "../UserInput/Buttons/SurveyButton";
import ArrowLeftSvg from "../EditableSvgs/ArrowLeftSvg";
import AdvisorSvg from "../EditableSvgs/AdvisorSvg";
import { FontSource } from "@fhc/shared/constants";

const google_fonts_url_base = "https://fonts.googleapis.com/css2?family=";

function cleanupURL(url) {
    url = url.trim();
    return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
}

const SurveyResults = ({
    themeColors,
    questionResponses,
    surveyResults,
    appointment,
    bookAppointment,
    resetAssessment,
    emailResults,
    collector,
}) => {
    const [showRememberYourResultsForm, setShowRememberYourResultsForm] = useState(false);

    const recommendations = Array.isArray(surveyResults.recommendations)
        ? surveyResults.recommendations
        : [...(surveyResults.recommendations?.goals ?? []), ...(surveyResults.recommendations?.improvements ?? [])];

    const [collapseHeader, setCollapseHeader] = useState(null); // null used to prevent animation on first render
    useEffect(() => {
        const handleScroll = () => setCollapseHeader(window.scrollY > 0);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const bookAppointmentRef = useRef(null);
    const headerRef = useRef(null);

    /**
     * Scrolls to the appointment form.
     * This is needed instead of just using an anchor tag because the header is sticky,
     * so it would scroll too far, and the height of the header is dynamic so it can't be hardcoded.
     */
    const scrollToAppointmentForm = () => {
        const headerHeight = headerRef.current.getBoundingClientRect().height;
        const targetPosition =
            bookAppointmentRef.current.getBoundingClientRect().top + window.scrollY - headerHeight - 32;

        window.scrollTo({
            top: targetPosition,
            behavior: "smooth",
        });
    };

    return (
        <div style={{ background: themeColors.primaryColor }} className="w-full">
            {/* Animations for sticky header */}
            <style>
                {
`${themeColors.customHeaderFont.source === FontSource.GOOGLE_FONT ?
`@import url('${google_fonts_url_base}${themeColors.customHeaderFont.name}');` : ''
}
${themeColors.customBodyFont.source === FontSource.GOOGLE_FONT && (themeColors.customHeaderFont.source !== FontSource.GOOGLE_FONT || themeColors.customHeaderFont.name !== themeColors.customBodyFont.name) ? 
`@import url('${google_fonts_url_base}${themeColors.customBodyFont.name}');` : ''
}
${themeColors.customHeaderFont.source === FontSource.FILE ? 
`${themeColors.customHeaderFont.file ?
`@font-face {
    font-family: '${themeColors.customHeaderFont.name}';
    src: url('${themeColors.customHeaderFont.file.data}');
}` : ""
}` : ""
}
${themeColors.customBodyFont.source === FontSource.FILE ? 
`${themeColors.customBodyFont.file ? 
`@font-face {
    font-family: '${themeColors.customBodyFont.name}';
    src: url('${themeColors.customBodyFont.file.data}');
}` : ""
}` : ""
}
@keyframes collapse {
    from {
        height: 85px;
        padding: 10px 20px;
        box-shadow: var(--box-shadow-collapsed);
    }
    to {
        height: 150px;
        padding: 20px 20px;
        box-shadow: var(--box-shadow-expanded);
    }
}
@keyframes expand {
    from {
        height: 150px;
        padding: 20px 20px;
        box-shadow: var(--box-shadow-expanded);
    }
    to {
        height: 85px;
        padding: 10px 20px;
        box-shadow: var(--box-shadow-collapsed);
    }
}
.header-text {
    font-family: '${themeColors.customHeaderFont.name}', '${themeColors.customHeaderFont.type}';
}
input,
.c-box,
.body-text,
.MuiTypography-root,
.MuiButton-textPrimary {
    font-family: '${themeColors.customBodyFont.name}', '${themeColors.customBodyFont.type}';
}

:root {
--box-shadow-expanded: 0 2px 16px rgba(0, 0, 0, 0.0);
--box-shadow-collapsed: 0 2px 16px rgba(0, 0, 0, 0.4);
}
`}
            </style>

            <div
                style={{
                    width: "600px",
                    position: "absolute",
                    right: "0px",
                    top: "164px",
                }}
            >
                <ArrowLeftSvg
                    primaryColor={themeColors.primaryColor}
                    secondaryColor={themeColors.secondaryColor}
                    viewBoxX={-230}
                    hideChevrons={themeColors.hideChevrons}
                />
            </div>

            {/* Sticky Header - Logo and speak with an advisor button */}
            <div
                ref={headerRef}
                className={`flex items-center justify-between px-5 md:px-20 py-5`}
                style={{
                    width: "100%",
                    position: "sticky",
                    top: 0,
                    left: 0,
                    zIndex: 1000, // Ensures it stays on top of other elements
                    backgroundColor: themeColors.primaryColor, // Matches the background color to the rest of the page
                    padding: "10px 20px",
                    animation:
                        collapseHeader === null
                            ? "none"
                            : collapseHeader
                              ? "expand 0.33s ease forwards"
                              : "collapse .4s ease-out forwards",
                }}
            >
                <SurveyLogo src={themeColors.mainLogo} />
                {collector?.advisor_direct_link ? (
                    collector?.custom_button_text &&
                    collector?.custom_button_link && (
                        <SurveyResultsButton
                            colorTheme={themeColors}
                            href={cleanupURL(collector.custom_button_link)}
                            target="_blank"
                            rel="noopener"
                        >
                            {collector.custom_button_text}
                        </SurveyResultsButton>
                    )
                ) : (
                    <SurveyResultsButton colorTheme={themeColors} handleOnClick={scrollToAppointmentForm}>
                        Speak with an advisor
                    </SurveyResultsButton>
                )}
            </div>

            {/* Financial health score, recommendations, and book an appointment form */}
            <main>
                <Box
                    display="flex"
                    sx={{ my: { xs: 5, sm: 8, md: 10 } }}
                    justifyContent="center"
                    flexDirection="column"
                    alignItems="center"
                    className="h-full"
                >
                    {/* Financial health score */}
                    <Container sx={{ px: 0 }} className="-mt-10 mb-7">
                        <Grid container sx={{ px: 3, mb: 10 }} className="w-full">
                            <Grid item sm={12} md={6} lg={6} className="w-full">
                                <div className="w-full flex items-center justify-center mb-10 md:mb-0">
                                    <div className="relative w-[362px] h-[362px]">
                                        <div className="bg-slate-300 rounded-full w-full h-full  border-[10px] border-slate-300 relative">
                                            <div
                                                style={{
                                                    borderColor: themeColors.buttonBackgroundColor,
                                                    color: themeColors.buttonTextColor,
                                                }}
                                                className="absolute w-full h-full bg-transparent border-[10px] border-t-slate-300 border-r-slate-300  rounded-full"
                                            >
                                                <div className="bg-white z-10 absolute w-full h-full  border-[10px]  border-slate-300 rounded-full"></div>
                                            </div>
                                        </div>

                                        <div className="z-20 absolute inset-0 w-full h-full flex justify-center items-center flex-col space-y-[5px]">
                                            <div className="body-text text-slate-600 font-bold text-2xl bg-white">
                                                Your score
                                            </div>
                                            <div
                                                style={{ color: themeColors.buttonBackgroundColor }}
                                                className="header-text font-bold text-[95.4725px] "
                                            >
                                                {surveyResults?.financial_score}
                                            </div>
                                            <div className="body-text bg-white text-center leading-5">
                                                <div>
                                                    <small>How do you compare?</small>
                                                </div>
                                                <div>
                                                    <small>The average* score is 500</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Grid>
                            <Grid item sm={12} md={6} lg={6}>
                                <Box className="relative">
                                    <div className="z-50">
                                        <h1 className="header-text font-semibold text-3xl md:text-5xl leading-[60px] text-white mb-[30px] text-center md:text-left">
                                            {surveyResults.category}
                                        </h1>
                                        <div className="body-text text-white md:text-[26px] md:leading-[40px] mb-[30px] text-center md:text-left text-base font-[500]">
                                            {surveyResults.description?.replaceAll(
                                                "financial health",
                                                themeColors.metricName,
                                            )}
                                        </div>
                                        <div className="flex gap-5">
                                            <SurveyResultsButton
                                                colorTheme={themeColors}
                                                handleOnClick={() => setShowRememberYourResultsForm(true)}
                                            >
                                                Remember your results
                                            </SurveyResultsButton>
                                            <SurveyResultsButton
                                                forceRounded={themeColors.roundedButtons}
                                                handleOnClick={resetAssessment}
                                            >
                                                Reset assessment
                                            </SurveyResultsButton>
                                        </div>
                                    </div>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>

                    {/* Recommendations */}
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            mb: 7,
                            backgroundImage: `url(${themeColors.recommendationsImage})`,
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center top",
                            // Themeable overlay for the image
                            "&::before": {
                                content: "''", // Need to do this or the overlay won't be visible
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: themeColors.secondaryColor,
                                opacity: 0.7,
                            },
                        }}
                    >
                        <Grid container>
                            <Grid item xs={12} md={4} />
                            <Grid item xs={12} md={4}>
                                <Box
                                    sx={{
                                        position: "relative", // Position defaults to 'static' which causes z-index to have no effect
                                        zIndex: 1, // Text must have a higher z-index than the transparent overlay (if you don't do this, the overlay is applied to the text and the text cannot be highlighted for copying and pasting)
                                        px: 3,
                                        my: 5,
                                    }}
                                >
                                    <h2 className="header-text text-center text-5xl font-semibold text-white mb-4">
                                        Your next steps
                                    </h2>
                                    <div className="body-text text-center text-[26px]  text-white mb-10 font-[400]">
                                        Here are some things to consider:
                                    </div>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4} className="flex items-center justify-center">
                                {themeColors.nextStepsButton?.link && (
                                    <Box sx={{ position: "relative", zIndex: 1, marginBottom: { xs: 5, md: 0 } }}>
                                        <SurveyResultsButton
                                            href={cleanupURL(themeColors.nextStepsButton.link)}
                                            rel="noopener"
                                            target="_blank"
                                        >
                                            {themeColors.nextStepsButton.text}
                                        </SurveyResultsButton>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                        <Grid container>
                            {recommendations?.map((recommendation, index) => (
                                <Grid
                                    item
                                    sm={12}
                                    md={12 / recommendations.length}
                                    lg={12 / recommendations.length}
                                    key={index}
                                    sx={{
                                        color: themeColors.buttonTextColor,
                                        background: themeColors.buttonBackgroundColor,
                                        opacity: "0.9",
                                        borderRight: index !== recommendations.length - 1 && "1px solid white",
                                    }}
                                    className="px-[48px] py-[38px] md:px-[56px] md:py-[70px] text-center grid grid-cols-3 md:flex md:flex-col items-center gap-y-4 w-full"
                                >
                                    <div
                                        style={{ color: themeColors.buttonBackgroundColor }}
                                        className="header-text rounded-full w-20 h-20 text-4xl bg-white flex items-center justify-center font-bold"
                                    >
                                        0{index + 1}
                                    </div>
                                    <div className="body-text col-span-2 text-white text-[14.95px] md:text-[28px] leading-normal md:leading-10 font-[500] ">
                                        {recommendation}
                                    </div>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* Book an appointment form */}
                    {collector?.advisor_direct_link || (
                        <div
                            id="book-appointment-form"
                            ref={bookAppointmentRef}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "100px",
                                width: "100%",
                            }}
                        >
                            <div className="hidden 2xl:flex" style={{ width: "600px" }}>
                                <AdvisorSvg
                                    primaryColor={themeColors.primaryColor}
                                    secondaryColor={themeColors.secondaryColor}
                                    hideChevrons={themeColors.hideChevrons}
                                    advisorImage={themeColors.advisorImage}
                                />
                            </div>
                            <div className="flex space-y-7 flex-col z-50 py-20 md:px-20 mx-auto">
                                <h2 className="header-text text-5xl text-white font-semibold max-w-[500px]">
                                    Review your results with an advisor
                                </h2>
                                <div className="body-text text-white text-2xl max-w-[650px] font-normal">
                                    An advisor will get in touch with you to find ways to meet your financial goals
                                </div>

                                {!appointment || appointment.appointment_link_expires_in <= 0 ? (
                                    <>
                                        {/* Show expiry explanation if recently expired (< 1 week) */}
                                        {appointment?.appointment_link_expires_in <= 0 &&
                                            appointment?.appointment_link_expires_in > -7 * 24 * 60 * 60 * 1000 && (
                                                <BookedAppointmentCard themeColors={themeColors}>
                                                    <div className="body-text text-center md:text-base text-xs p-6">
                                                        Your temporary link has expired, please re-submit to book an
                                                        appointment.
                                                    </div>
                                                </BookedAppointmentCard>
                                            )}
                                        {/* Show form if no appointment or expired link */}
                                        <BookAppointmentForm
                                            themeColors={themeColors}
                                            bookAppointment={bookAppointment}
                                        />
                                    </>
                                ) : appointment?.appointment_link ? (
                                    <BookedAppointmentCard themeColors={themeColors}>
                                        <div className="text-center md:text-base text-xs p-6">
                                            Please visit the link below to complete your appointment:
                                        </div>
                                        <div className="text-center bg-white rounded-b-lg p-6">
                                            <a
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 md:text-base text-xs"
                                                href={appointment.appointment_link}
                                            >
                                                {appointment.appointment_link.slice(0, 41)}...
                                            </a>
                                        </div>
                                    </BookedAppointmentCard>
                                ) : appointment?.mail_to ? (
                                    <BookedAppointmentCard themeColors={themeColors}>
                                        <div className="text-center md:text-base text-xs p-6">
                                            We're sorry, an error has occurred while booking your appointment. You can
                                            still contact an advisor using the link below.
                                            <br />
                                            <br />
                                            <SurveyButton
                                                size="md"
                                                href={`mailto:${appointment.mail_to}?subject=${encodeURIComponent(`${themeColors.appName} - Appointment request`)}`}
                                            >
                                                {appointment.mail_to}
                                            </SurveyButton>
                                        </div>
                                    </BookedAppointmentCard>
                                ) : (
                                    <BookedAppointmentCard themeColors={themeColors}>
                                        <div className="text-center md:text-base p-6">
                                            <b className="text-[14pt]">
                                                An advisor will reach out to you to discuss your{" "}
                                                {themeColors.metricName}
                                            </b>
                                        </div>
                                    </BookedAppointmentCard>
                                )}
                            </div>
                        </div>
                    )}
                </Box>

                {/* Assessment summary */}
                <div
                    className="w-full px-20 py-16"
                    style={{ color: themeColors.buttonTextColor, background: themeColors.buttonBackgroundColor }}
                >
                    <AssessmentSummary
                        questions={questionResponses.questions}
                        responses={questionResponses.responses}
                    ></AssessmentSummary>
                </div>
            </main>

            <footer>
                {/* Copyright */}
                <Box sx={{ backgroundColor: "white", width: "100%" }} display="flex" className="bg-white">
                    <Container>
                        <Box display="flex" alignItems="center" justifyContent="center" sx={{ my: 2 }}>
                            <Typography sx={{ fontWeight: 400, fontSize: 14.02, color: "#455468" }}>
                                {COPYRIGHT_MESSAGE}
                            </Typography>
                        </Box>
                    </Container>
                </Box>
            </footer>

            <RememberYourResults
                surveyResults={surveyResults}
                themeColors={themeColors}
                onClose={() => setShowRememberYourResultsForm(false)}
                show={showRememberYourResultsForm}
                emailResults={emailResults}
            />
        </div>
    );
};

export default SurveyResults;
